import React, { useState } from 'react';
import { DateRangePicker } from '../dashboard/DateRangePicker';
import { GranularitySelector, Granularity } from '../dashboard/GranularitySelector';
import { DisputesFilters } from './DisputesFilters';
import { EmptyState } from './EmptyState';
import { ChevronDownIcon } from 'lucide-react';
interface DateRange {
  start: Date;
  end: Date;
}
interface DisputesTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
export function DisputesTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: DisputesTabProps) {
  // New filter states
  const [disputeDateBasis, setDisputeDateBasis] = useState<'dispute-date' | 'payment-date'>('dispute-date');
  const [paymentCountBasis, setPaymentCountBasis] = useState<'payment-count' | 'payment-volume'>('payment-count');
  const [includeEFW, setIncludeEFW] = useState<'include' | 'exclude'>('include');
  // Existing filter states
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [cardCountries, setCardCountries] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [additionalFilters, setAdditionalFilters] = useState<string[]>([]);
  // Calculate previous period date range
  const getPreviousPeriodRange = () => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const prevStart = new Date(dateRange.start);
    prevStart.setDate(prevStart.getDate() - days);
    const prevEnd = new Date(dateRange.end);
    prevEnd.setDate(prevEnd.getDate() - days);
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    };
    return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
  };
  const handleClearFilters = () => {
    setDisputeDateBasis('dispute-date');
    setPaymentCountBasis('payment-count');
    setIncludeEFW('include');
    setCardTypes([]);
    setCardCountries([]);
    setCardBrands([]);
    setAdditionalFilters([]);
  };
  return <div className="space-y-6" data-id="element-905">
      {/* All Filters - Compact Layout */}
      <div className="space-y-3" data-id="element-906">
        {/* First Row: Date Range and Granularity */}
        <div className="flex flex-wrap items-center gap-3" data-id="element-907">
          <DateRangePicker value={dateRange} onChange={onDateRangeChange} data-id="element-908" />
          <GranularitySelector value={granularity as Granularity} onChange={onGranularityChange} data-id="element-909" />
        </div>

        {/* Disputes Filters (includes both rows) */}
        <DisputesFilters disputeDateBasis={disputeDateBasis} onDisputeDateBasisChange={setDisputeDateBasis} paymentCountBasis={paymentCountBasis} onPaymentCountBasisChange={setPaymentCountBasis} includeEFW={includeEFW} onIncludeEFWChange={setIncludeEFW} cardTypes={cardTypes} onCardTypesChange={setCardTypes} cardCountries={cardCountries} onCardCountriesChange={setCardCountries} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} additionalFilters={additionalFilters} onAdditionalFiltersChange={setAdditionalFilters} data-id="element-910" />
      </div>

      {/* Key Metrics Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-911">
        <div className="p-6 border-b border-gray-200" data-id="element-912">
          <div className="flex items-center justify-between" data-id="element-913">
            <div className="flex items-center gap-4" data-id="element-914">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-915">
                Key metrics for cards
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-916">
                <span data-id="element-917">Compared to</span>
                <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-918">
                  Previous period
                  <ChevronDownIcon size={16} data-id="element-919" />
                </button>
                <span className="text-gray-500" data-id="element-920">
                  {getPreviousPeriodRange()}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-12" data-id="element-921">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-923">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-922" />
        </div>
      </div>

      {/* Card disputes breakdown */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-924">
        <div className="p-6 border-b border-gray-200" data-id="element-925">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-926">
            Card disputes breakdown
          </h2>
        </div>

        <div className="p-12" data-id="element-927">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-929">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-928" />
        </div>
      </div>

      {/* Monthly submission performance */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-930">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-931">
          <div data-id="element-932">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-933">
              Monthly submission performance
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-934">
              A detailed view of submission performance metrics and trends over
              the past 12 months.
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-935">
            All reasons
            <ChevronDownIcon size={16} data-id="element-936" />
          </button>
        </div>

        <div className="p-12" data-id="element-937">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-939">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-938" />
        </div>
      </div>

      {/* Monthly dispute outcomes */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-940">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-941">
          <div data-id="element-942">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-943">
              Monthly dispute outcomes
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-944">
              A breakdown of how card disputes were resolved over the past 12
              months.
            </p>
          </div>
          <button className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-945">
            All reasons
            <ChevronDownIcon size={16} data-id="element-946" />
          </button>
        </div>

        <div className="p-12" data-id="element-947">
          <EmptyState message="No disputes matching this selection." submessage={<>
                <button onClick={handleClearFilters} className="text-blue-600 hover:underline" data-id="element-949">
                  Clear filters
                </button>{' '}
                or select another date range and try again.
              </>} data-id="element-948" />
        </div>
      </div>
    </div>;
}